import { BaseDataProvider } from "./baseProvider";
import type { Promotion, PromotionInput } from "../types/api";

/**
 * /admin/promotions — markup promotions (regular and lightning).
 *
 * The API has list/create/update only: there is no GET-by-id (getOne filters
 * the list) and no delete (switch a promotion off with isActive=false instead,
 * so its history still counts for later promotions' reference prices).
 *
 * Records carry an extra `cities` field mirroring `regions` so the form's city
 * picker can edit it; only the fields the API accepts are ever sent back.
 */
type PromotionRecord = Promotion & {
  cities: Array<{
    regionId: string;
    name: string;
    countryCode?: string | null;
  }>;
};

const toRecord = (p: Promotion): PromotionRecord => ({
  ...p,
  cities: (p.regions ?? []).map((r) => ({
    regionId: r.regionId,
    name: r.name,
    countryCode: r.countryCode ?? null,
  })),
});

/** Form values as react-admin hands them over (inputs may give strings or Dates). */
export interface PromotionFormValues {
  name?: string;
  startsAt?: string | Date;
  endsAt?: string | Date;
  minDiscountPercent?: number | string;
  maxDiscountPercent?: number | string;
  minMarginPercent?: number | string;
  maxMarginPercent?: number | string;
  listMarkupPercent?: number | string | null;
  coverageByStars?: Record<string, number>;
  cities?: Array<{ regionId: string | number; name?: string }>;
  isLightning?: boolean;
  discountType?: "percentage" | "fixed";
  fixedDiscountAmount?: number | string | null;
  fixedDiscountPer?: "stay" | "night";
  popupEnabled?: boolean;
  popupTitle?: string | null;
  popupMessage?: string | null;
  isActive?: boolean;
}

interface ListParams {
  pagination?: { page: number; perPage: number };
  filter?: { type?: string; status?: string; q?: string };
}

const num = (v: unknown): number | undefined =>
  v === "" || v == null ? undefined : Number(v);

/** Only the fields the backend accepts, in the backend's units. */
export const toPromotionInput = (
  data: PromotionFormValues = {},
): PromotionInput => {
  const input: PromotionInput = {
    name: data.name?.trim(),
    startsAt: data.startsAt ? new Date(data.startsAt).toISOString() : undefined,
    endsAt: data.endsAt ? new Date(data.endsAt).toISOString() : undefined,
    minDiscountPercent: num(data.minDiscountPercent),
    maxDiscountPercent: num(data.maxDiscountPercent),
    minMarginPercent: num(data.minMarginPercent),
    maxMarginPercent: num(data.maxMarginPercent),
    listMarkupPercent:
      data.listMarkupPercent === "" || data.listMarkupPercent == null
        ? null
        : Number(data.listMarkupPercent),
    coverageByStars: data.coverageByStars,
    isLightning: !!data.isLightning,
    discountType: data.discountType === "fixed" ? "fixed" : "percentage",
    popupEnabled: data.popupEnabled ?? true,
    popupTitle: data.popupTitle?.trim() || null,
    popupMessage: data.popupMessage?.trim() || null,
    isActive: data.isActive ?? true,
  };
  if (input.discountType === "fixed") {
    input.fixedDiscountAmount = num(data.fixedDiscountAmount) ?? null;
    input.fixedDiscountPer =
      data.fixedDiscountPer === "night" ? "night" : "stay";
    input.fixedDiscountCurrency = "EUR";
    // The percentage range is unused for fixed promotions.
    input.minDiscountPercent = input.minDiscountPercent ?? 0;
    input.maxDiscountPercent = input.maxDiscountPercent ?? 0;
  }
  if (Array.isArray(data.cities)) {
    input.cities = data.cities.map((c) => ({
      regionId: String(c.regionId),
      name: c.name,
    }));
  }
  return input;
};

export class PromotionDataProvider extends BaseDataProvider {
  constructor() {
    super("promotions");
  }

  private async fetchAll(): Promise<PromotionRecord[]> {
    const response = (await this.makeRequest("promotions")) as Promotion[];
    return (Array.isArray(response) ? response : []).map(toRecord);
  }

  async getList(_resource: string, params: ListParams) {
    const { page, perPage } = params.pagination || { page: 1, perPage: 25 };
    const filter = params.filter || {};
    const now = Date.now();
    let rows = await this.fetchAll();

    if (filter.type === "lightning") rows = rows.filter((p) => p.isLightning);
    if (filter.type === "regular") rows = rows.filter((p) => !p.isLightning);
    if (filter.status)
      rows = rows.filter((p) => promotionStatus(p, now) === filter.status);
    if (filter.q) {
      const q = String(filter.q).toLowerCase();
      rows = rows.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.regions.some((r) => r.name.toLowerCase().includes(q)),
      );
    }

    const start = (page - 1) * perPage;
    return { data: rows.slice(start, start + perPage), total: rows.length };
  }

  async getOne(_resource: string, params: { id?: unknown }) {
    const found = (await this.fetchAll()).find((p) => p.id === params.id);
    if (!found) throw new Error(`Promotion ${params.id} not found`);
    return { data: found };
  }

  async getMany(_resource: string, params: { ids?: unknown }) {
    const ids = new Set((params.ids as unknown[]).map(String));
    return { data: (await this.fetchAll()).filter((p) => ids.has(p.id)) };
  }

  async getManyReference() {
    return { data: [], total: 0 };
  }

  async create(_resource: string, params: { data?: PromotionFormValues }) {
    const response = (await this.makeRequest("promotions", {
      method: "POST",
      body: JSON.stringify(toPromotionInput(params.data)),
      headers: new Headers({ "Content-Type": "application/json" }),
    })) as Promotion;
    return { data: toRecord(response) };
  }

  async update(
    _resource: string,
    params: { id?: unknown; data?: PromotionFormValues },
  ) {
    const response = (await this.makeRequest(`promotions/${params.id}`, {
      method: "PATCH",
      body: JSON.stringify(toPromotionInput(params.data)),
      headers: new Headers({ "Content-Type": "application/json" }),
    })) as Promotion;
    return { data: toRecord(response) };
  }

  /** Switch a promotion on or off without touching anything else. */
  async setActive(id: string, isActive: boolean) {
    const response = (await this.makeRequest(`promotions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ isActive }),
      headers: new Headers({ "Content-Type": "application/json" }),
    })) as Promotion;
    return toRecord(response);
  }

  async updateMany(
    _resource: string,
    params: { ids?: unknown; data?: PromotionFormValues },
  ) {
    const results = await Promise.all(
      (params.ids as unknown[])
        .map(String)
        .map((id) => this.setActive(id, !!params.data?.isActive)),
    );
    return { data: results.map((r) => r.id) };
  }

  async delete(_resource: string, params: { id?: unknown }): Promise<never> {
    throw new Error(
      `Promotion ${String(params?.id)} can't be deleted; switch it off instead.`,
    );
  }

  async deleteMany(
    _resource: string,
    params: { ids?: unknown },
  ): Promise<never> {
    const count = Array.isArray(params?.ids) ? params.ids.length : 0;
    throw new Error(
      `${count} promotion(s) can't be deleted; switch them off instead.`,
    );
  }
}

export type PromotionStatus = "live" | "scheduled" | "ended" | "inactive";

export const promotionStatus = (
  p: Pick<Promotion, "isActive" | "startsAt" | "endsAt">,
  now = Date.now(),
): PromotionStatus => {
  if (!p.isActive) return "inactive";
  if (now < new Date(p.startsAt).getTime()) return "scheduled";
  if (now >= new Date(p.endsAt).getTime()) return "ended";
  return "live";
};
